import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, Square, Loader2, CheckCircle2, PauseCircle, Globe } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Action } from "../kit/PublishBlockers";

const Fixes = ({ f, kid, reload }) => {
  const [busy, setBusy] = useState(null);
  const retry = () => http.post(`/ops/autopilot/retry/${kid}`);
  const again = async () => {
    setBusy("again");
    try { await retry(); toast.success("Sent back to Autopilot"); await reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  return (
    <ul className="ml-6 mt-1">
      {f.reasons.map((r) => (
        <li key={r.text} className="flex flex-wrap items-center gap-2 py-0.5 text-xs">
          <span className="text-stone-600">{r.text}</span>
          {r.actions.map((a) => <Action key={a.label} a={a} kid={kid} reload={reload} busy={busy} setBusy={setBusy} after={retry} tid="autopilot-fix" />)}
        </li>
      ))}
      <li className="py-0.5">
        <button data-testid={`autopilot-retry-${kid}`} disabled={!!busy || f.busy} onClick={again}
          className="text-xs font-semibold text-[#9A7B2C] underline hover:text-[#2B2A26] disabled:opacity-40">
          {f.busy ? "Working on it…" : f.reasons.length ? "Try again in Autopilot" : "Fixed — try again in Autopilot"}
        </button>
      </li>
    </ul>
  );
};

const Result = ({ r, i, fix, reload }) => (
  <li data-testid={`autopilot-result-${i}`} className="border-t border-stone-100 py-1.5 text-sm">
    <div className="flex items-start gap-2">
      {r.state === "approved" ? <CheckCircle2 size={14} className="mt-0.5 text-emerald-600" /> : <PauseCircle size={14} className="mt-0.5 text-amber-600" />}
      {r.id ? <Link to={`/kits/${r.id}`} className="font-bold text-[#2B2A26] hover:underline">{r.destination}</Link> : <b>{r.destination}</b>}
      <span className="text-stone-500">{r.state === "approved" ? "Approved — ready for you to publish" : `Parked: ${r.why}`}</span>
      {r.cost !== undefined && <span data-testid={`autopilot-result-cost-${i}`} className="ml-auto text-xs tabular-nums text-stone-500">${r.cost.toFixed(2)}</span>}
    </div>
    {r.state !== "approved" && fix && !fix.live && !fix.finalised && <Fixes f={fix} kid={r.id} reload={reload} />}
  </li>
);

export const Autopilot = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/ops/autopilot").then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = d?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);
  const go = async (path) => {
    setBusy(true);
    try {
      const r = await http.post(path);
      if (r.data.queued !== undefined) toast.success(`Autopilot started: ${r.data.queued} queued + ${r.data.waiting} built kit(s)`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const publishAll = async () => {
    if (!window.confirm(`Put all ${d.approved} approved kit(s) on sale now?`)) return;
    setBusy(true);
    try {
      const r = await http.post("/ops/autopilot/publish-approved");
      toast.success(`${r.data.published.length} kit(s) now live${r.data.skipped.length ? ` · ${r.data.skipped.length} skipped` : ""}`);
      r.data.skipped.forEach((x) => toast.warning(`${x.destination}: ${x.problem}`));
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setOvernight = async (v) => {
    setD((x) => ({ ...x, overnight: v }));
    try { await http.post("/ops/autopilot/overnight", { enabled: v }); toast.success(v ? "Overnight Autopilot on — runs after the nightly batch" : "Overnight Autopilot off"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  if (!d) return null;
  const res = d.results || [];
  const approved = res.filter((r) => r.state === "approved").length;
  return (
    <section data-testid="autopilot-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Rocket size={16} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Build-to-Approved Autopilot</b>
        <span data-testid="autopilot-status" className="text-sm text-stone-500">
          {running ? <><Loader2 size={12} className="inline animate-spin" /> {d.current ? `${d.current} — ${d.note || "working"}` : d.note || "Starting…"}</>
            : d.status === "paused" ? d.note
            : !d.can_start ? `Nothing waiting${d.status ? ` · last run: ${approved} approved, ${res.length - approved} parked` : ""}`
            : d.status ? `${d.can_start} kit(s) waiting · last run: ${approved} approved, ${res.length - approved} parked`
            : `${d.can_start} kit(s) can go: researches queued kits, fixes pages and house style, stops at Approved`}
          {running && ` · ${res.length}/${d.total || 0} done`}
        </span>
        {(d.spend > 0 || res.some((r) => r.cost !== undefined)) && (
          <span data-testid="autopilot-spend" className="rounded-sm bg-stone-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-stone-700">
            AI spend ${(d.spend || 0).toFixed(2)}{running && d.current_cost != null ? ` (this kit so far $${d.current_cost.toFixed(2)})` : ""}
          </span>
        )}
        {d.queue_paused && <span data-testid="autopilot-queue-paused" className="text-xs text-amber-700">Build queue is paused{d.queue_reason ? `: ${d.queue_reason}` : ""}</span>}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-stone-600">
            <Switch data-testid="autopilot-overnight-switch" checked={!!d.overnight} onCheckedChange={setOvernight} /> Run overnight
          </label>
          <button data-testid="autopilot-publish-approved-btn" disabled={busy || !d.approved} onClick={publishAll}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] transition-colors disabled:opacity-40">
            <Globe size={12} /> Publish all approved ({d.approved || 0})
          </button>
          {running ? (
            <button data-testid="autopilot-stop-btn" disabled={busy} onClick={() => go("/ops/autopilot/stop")}
              className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:bg-stone-100"><Square size={12} /> Stop</button>
          ) : (
            <button data-testid="autopilot-start-btn" disabled={busy || !d.can_start} onClick={() => go("/ops/autopilot")}
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black transition-colors disabled:opacity-40">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Start Autopilot (uses AI)
            </button>
          )}
        </div>
      </div>
      {res.length > 0 && <ul className="mt-2 max-h-64 overflow-y-auto">{[...res].reverse().map((r, i) => <Result key={`${r.id}-${i}`} r={r} i={i} fix={(d.fixes || {})[r.id]} reload={load} />)}</ul>}
    </section>
  );
};
